import {
  Box,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightAddon,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormLabelWithInfo } from "../../../../../common/input/FormLabelWithInfo";
import { useDebouncedSave } from "../hooks/useDebouncedSave";
import { useSaveField } from "../hooks/useSaveField";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { ProcedureType, type SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { PROCEDURE_TYPE_OPTIONS } from "../utils/publicProcurementOptions";

const DEFAULT_VALIDITY_MONTHS = 3;

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  previousStep: () => void;
  nextStep: () => void;
}

export const ProcedureStep = ({ eventId, event, previousStep, nextStep }: Props) => {
  const { t } = useTranslation();
  const { saveField, isSaving } = useSaveField(eventId);
  const { publicContext } = event;

  const [procedureType, setProcedureType] = useState<ProcedureType | "">(publicContext.procedureType ?? "");
  const [validityStr, setValidityStr] = useState<string>(
    String(publicContext.eFormsNoticeData?.tenderValidityMonths ?? DEFAULT_VALIDITY_MONTHS)
  );

  useEffect(() => {
    if (publicContext.eFormsNoticeData?.tenderValidityMonths == null) {
      saveField({
        eFormsNoticeData: {
          value: { ...publicContext.eFormsNoticeData, tenderValidityMonths: DEFAULT_VALIDITY_MONTHS },
        },
      });
    }
  }, [publicContext.eFormsNoticeData, saveField]);

  const debouncedSaveValidity = useDebouncedSave(
    useCallback(
      (months: number) =>
        saveField({
          eFormsNoticeData: {
            value: { ...publicContext.eFormsNoticeData, tenderValidityMonths: months },
          },
        }),
      [saveField, publicContext.eFormsNoticeData]
    )
  );

  return (
    <Box pb="8" pt="10">
      <FormControl mb="8">
        <FormLabel>{t("Procedure type")} {isSaving("procedureType") && <Spinner size="xs" />}</FormLabel>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
          {PROCEDURE_TYPE_OPTIONS.map((option) => (
            <SelectableCard
              key={option.value}
              isSelected={procedureType === option.value}
              isDisabled={!option.enabled}
              disabledLabel={t("Coming soon") ?? undefined}
              onClick={() => {
                if (option.enabled) {
                  setProcedureType(option.value);
                  saveField({ procedureType: option.value });
                }
              }}
            >
              <Text fontWeight="semibold" fontSize="sm" color={option.enabled ? "smText" : "smMuted"}>
                {t(option.labelKey)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t(option.descriptionKey)}
              </Text>
            </SelectableCard>
          ))}
        </SimpleGrid>
      </FormControl>

      <FormControl mb="8">
        <FormLabelWithInfo
          tooltip={t("Specifies how long suppliers are bound by their bid. Common practice is 3 or 6 months.")}
          suffix={isSaving("eFormsNoticeData") && <Spinner size="xs" ml="1" />}
        >
          {t("Tender validity period")}
        </FormLabelWithInfo>
        <InputGroup maxW="250px">
          <NumberInput
            value={validityStr}
            min={1}
            max={120}
            w="full"
            onChange={(valueAsString, valueAsNumber) => {
              setValidityStr(valueAsString);
              if (!isNaN(valueAsNumber) && valueAsNumber >= 1) {
                debouncedSaveValidity(valueAsNumber);
              }
            }}
          >
            <NumberInputField borderRightRadius="0" />
          </NumberInput>
          {/* t('month_one') t('month_other') */}
          <InputRightAddon color="smMuted" fontSize="sm">
            {t("month", { count: Number(validityStr) || 1 })}
          </InputRightAddon>
        </InputGroup>
      </FormControl>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={t("Summary")} />
    </Box>
  );
};
